import {
  casesService,
  extractDocTypesArrayFromGetServiceResponse,
  type CaseDetailData,
  type CaseDocumentTypeItem,
  type CaseQuestionItem,
  type CaseStepInfoItem,
  type StepInOrderItem,
} from "services/cases.service";
import {
  isCaseQuestionMandatoryForActionBanner,
  isCaseQuestionRequired,
} from "../components/CaseDetailQandATab/utils";
import {
  isStepWorkflowCompletedItem,
  stepInOrderToDisplayItem,
} from "../components/CaseDetailOverviewTab/utils";
import {
  getCaseDocumentRequirementTypeId,
  mapServiceRawDocTypesToCatalog,
  type DocumentTypeOption,
} from "../components/CaseDetailDocumentTab/useCaseDocuments";

export type CaseActionSession = {
  business_id: string;
  device_token: string;
  session_id: string;
  service_id?: string | number | null;
};

export const CONFIRM_CASE_BLOCKED_MESSAGE =
  "Complete all required questions and upload all mandatory documents before confirming this case.";

export const MARK_COMPLETED_BLOCKED_MESSAGE =
  "Complete all process steps before marking this case as completed.";

const LEVELS = [1, 2, 3] as const;

function isTruthyFlag(v: unknown): boolean {
  return v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
}

function isExplicitFalseFlag(v: unknown): boolean {
  if (v === false || v === 0) return true;
  const normalized = String(v ?? "")
    .trim()
    .toLowerCase();
  return normalized === "false" || normalized === "0";
}

function isCaseDocumentMandatoryForActionBanner(cd: CaseDocumentTypeItem): boolean {
  const r = cd as Record<string, unknown>;
  if (r.is_active === false || cd.is_active === false) return false;
  if (!isTruthyFlag(r.is_active ?? cd.is_active)) return false;
  if (
    isTruthyFlag(r.is_mandatory) ||
    isTruthyFlag(r.mandatory) ||
    isTruthyFlag(r.required) ||
    isTruthyFlag(r.is_required)
  ) {
    return true;
  }
  if (
    isExplicitFalseFlag(r.is_mandatory) ||
    isExplicitFalseFlag(r.mandatory) ||
    isExplicitFalseFlag(r.required) ||
    isExplicitFalseFlag(r.is_required)
  ) {
    return false;
  }
  if (cd.is_optional === true || r.is_optional === true) return false;
  if (cd.is_optional === false || r.is_optional === false) return true;
  if (cd.is_optional !== undefined && cd.is_optional !== null) {
    return !isTruthyFlag(cd.is_optional);
  }
  if (r.is_optional !== undefined && r.is_optional !== null) {
    return !isTruthyFlag(r.is_optional);
  }
  return false;
}

function isCaseDocumentUploadMandatory(cd: CaseDocumentTypeItem): boolean {
  const r = cd as Record<string, unknown>;
  if (r.is_active === false || cd.is_active === false) return false;
  if (
    isTruthyFlag(r.is_mandatory) ||
    isTruthyFlag(r.mandatory) ||
    isTruthyFlag(r.required) ||
    isTruthyFlag(r.is_required)
  ) {
    return true;
  }
  if (
    isExplicitFalseFlag(r.is_mandatory) ||
    isExplicitFalseFlag(r.mandatory) ||
    isExplicitFalseFlag(r.required) ||
    isExplicitFalseFlag(r.is_required)
  ) {
    return false;
  }
  if (cd.is_optional !== undefined && cd.is_optional !== null) {
    return !isTruthyFlag(cd.is_optional);
  }
  if (r.is_optional !== undefined && r.is_optional !== null) {
    return !isTruthyFlag(r.is_optional);
  }
  return true;
}

function hasUploadedForDocType(cd: CaseDocumentTypeItem): boolean {
  return (cd.user_documents?.length ?? 0) > 0 || (cd.user_uploaded?.length ?? 0) > 0;
}

async function fetchServiceDocTypeCatalog(
  serviceId: string | number,
  sessionPayload: { business_id: string; device_token: string; session_id: string }
): Promise<DocumentTypeOption[]> {
  try {
    const res = await casesService.getServiceDocTypes({
      service_id: serviceId,
      ...sessionPayload,
    });
    const rawList = extractDocTypesArrayFromGetServiceResponse(res);
    return mapServiceRawDocTypesToCatalog(rawList);
  } catch {
    return [];
  }
}

function mandatoryDocumentsSatisfied(
  caseDocuments: CaseDocumentTypeItem[],
  serviceCatalog?: DocumentTypeOption[]
): boolean {
  if (serviceCatalog && serviceCatalog.length > 0) {
    for (const meta of serviceCatalog) {
      if (!meta.is_active || meta.is_optional) continue;
      const cd = caseDocuments.find(
        (d) => getCaseDocumentRequirementTypeId(d) === meta.doc_type_id
      );
      if (!cd || !hasUploadedForDocType(cd)) return false;
    }
    return true;
  }
  if (caseDocuments.length === 0) return true;
  const mandatory = caseDocuments.filter((cd) => isCaseDocumentUploadMandatory(cd));
  if (mandatory.length === 0) return true;
  return mandatory.every(hasUploadedForDocType);
}

function isAnsweredValue(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "boolean") return true;
  if (typeof v === "number") return Number.isFinite(v);
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function isQuestionAnswered(q: { [key: string]: unknown }): boolean {
  return (
    isAnsweredValue(q.answer) ||
    isAnsweredValue(q.single_answer) ||
    isAnsweredValue(q.text_answer) ||
    isAnsweredValue(q.boolean_ans) ||
    isAnsweredValue(q.multi_answer)
  );
}

export function isCaseStateCreated(caseState: string | undefined): boolean {
  return (caseState ?? "").toUpperCase().trim().replace(/\s+/g, "-") === "CREATED";
}

export function isCaseStateInProgressForSteps(caseState: string | undefined): boolean {
  const normalized = (caseState ?? "").toUpperCase().trim().replace(/\s+/g, "-");
  return (
    normalized === "IN-PROGRESS" ||
    (caseState ?? "").toUpperCase().trim().replace(/\s+/g, "") === "INPROGRESS" ||
    (caseState ?? "").toUpperCase().trim() === "PENDING"
  );
}

function stepHasOutstandingWork(step: CaseStepInfoItem): boolean {
  if (isStepWorkflowCompletedItem(step)) return false;

  const info = step.task_details?.case_step_todolist_info;
  const total = typeof info?.total_tasks === "number" ? info.total_tasks : 0;
  const completed = typeof info?.completed === "number" ? info.completed : 0;
  if (total > 0 && completed < total) return true;

  const todoList = step.task_details?.case_step_todolist ?? [];
  const compulsoryTasks = todoList.filter((t) => t.is_compulsory === true);
  if (compulsoryTasks.length > 0 && !compulsoryTasks.every((t) => t.is_checked === true)) {
    return true;
  }

  return true;
}

function caseCurrStateIndicatesFullyCompleted(caseState?: string): boolean {
  const n = (caseState ?? "").toUpperCase().trim().replace(/\s+/g, "-");
  return n === "COMPLETED" || n === "COMPLETE";
}

function stepsFromGetCaseStepsResponse(
  data:
    | {
        case_step_info?: CaseStepInfoItem[];
        steps_in_order?: StepInOrderItem[];
      }
    | null
    | undefined
): CaseStepInfoItem[] {
  const info = data?.case_step_info ?? [];
  if (info.length > 0) return info;
  const inOrder = data?.steps_in_order;
  if (inOrder?.length) return inOrder.map(stepInOrderToDisplayItem);
  return [];
}

export async function evaluateConfirmCaseEligibility(
  caseId: string,
  caseState: string | undefined,
  session: CaseActionSession
): Promise<boolean> {
  if (!isCaseStateCreated(caseState)) return false;
  try {
    const { business_id, device_token, session_id, service_id } = session;
    const sessionPayload = { business_id, device_token, session_id };
    const catalogPromise =
      service_id !== undefined && service_id !== null && String(service_id).trim() !== ""
        ? fetchServiceDocTypeCatalog(service_id, sessionPayload)
        : Promise.resolve([] as DocumentTypeOption[]);

    const [docRes, serviceCatalog, ...levelRes] = await Promise.all([
      casesService.getCaseUploadedDocuments({
        case_id: caseId,
        ...sessionPayload,
      }),
      catalogPromise,
      ...LEVELS.map((level) =>
        casesService.getCaseQuestions({
          case_id: caseId,
          level,
          ...sessionPayload,
        })
      ),
    ]);

    if (docRes?.success === false) return false;

    const caseDocuments = Array.isArray(docRes?.data?.case_documents)
      ? docRes.data.case_documents
      : [];
    const hasMandatoryDocuments = mandatoryDocumentsSatisfied(caseDocuments, serviceCatalog);

    const allRequiredAnswered = levelRes.every((res) => {
      if (res?.success === false) return false;
      const questions = (res?.data?.question_info ?? []) as CaseQuestionItem[];
      const required = questions.filter((q) => isCaseQuestionRequired(q));
      if (required.length === 0) return true;
      return required.every((q) => isQuestionAnswered(q as { [key: string]: unknown }));
    });

    return Boolean(hasMandatoryDocuments && allRequiredAnswered);
  } catch {
    return false;
  }
}

export async function getCaseHasMandatoryQAOrDocuments(
  caseId: string,
  session: CaseActionSession
): Promise<boolean> {
  try {
    const { business_id, device_token, session_id, service_id } = session;
    const sessionPayload = { business_id, device_token, session_id };
    const catalogPromise =
      service_id !== undefined && service_id !== null && String(service_id).trim() !== ""
        ? fetchServiceDocTypeCatalog(service_id, sessionPayload)
        : Promise.resolve([] as DocumentTypeOption[]);

    const [docRes, serviceCatalog, ...levelRes] = await Promise.all([
      casesService.getCaseUploadedDocuments({
        case_id: caseId,
        ...sessionPayload,
      }),
      catalogPromise,
      ...LEVELS.map((level) =>
        casesService.getCaseQuestions({
          case_id: caseId,
          level,
          ...sessionPayload,
        })
      ),
    ]);

    const caseDocuments = Array.isArray(docRes?.data?.case_documents)
      ? docRes.data.case_documents
      : [];
    const docSideMandatory =
      serviceCatalog.length > 0
        ? serviceCatalog.some((m) => m.is_active && !m.is_optional)
        : caseDocuments.some((cd) => isCaseDocumentMandatoryForActionBanner(cd));
    if (docSideMandatory) return true;

    return levelRes.some((res) => {
      if (res?.success === false) return false;
      const questions = (res?.data?.question_info ?? []) as CaseQuestionItem[];
      return questions.some((q) => isCaseQuestionMandatoryForActionBanner(q));
    });
  } catch {
    return true;
  }
}

export async function evaluateAllStepsCompleted(
  caseId: string,
  caseState: string | undefined,
  session: CaseActionSession
): Promise<boolean> {
  if (!isCaseStateInProgressForSteps(caseState)) return false;
  try {
    const { business_id, device_token, session_id } = session;
    const res = await casesService.getCaseSteps({
      case_id: caseId,
      business_id,
      device_token,
      session_id,
    });
    const steps = stepsFromGetCaseStepsResponse(res?.data);
    if (steps.length === 0) return false;
    return steps.every((s) => !stepHasOutstandingWork(s));
  } catch {
    return false;
  }
}

export async function reconcileCompletedCaseStatusWithStepProgress(
  detail: CaseDetailData,
  session: Pick<CaseActionSession, "business_id" | "device_token" | "session_id">
): Promise<CaseDetailData> {
  if (!caseCurrStateIndicatesFullyCompleted(detail.case_curr_state)) return detail;
  const caseId = String(detail.case_id ?? "").trim();
  if (!caseId) return detail;
  try {
    const { business_id, device_token, session_id } = session;
    const res = await casesService.getCaseSteps({
      case_id: caseId,
      business_id,
      device_token,
      session_id,
    });
    const steps = stepsFromGetCaseStepsResponse(res?.data);
    if (steps.length === 0) return detail;
    if (!steps.some(stepHasOutstandingWork)) return detail;
    return { ...detail, case_curr_state: "IN-PROGRESS" };
  } catch {
    return detail;
  }
}
